/**
 * PlayerRow — compact row for a player + team + N stat values.
 *
 * Unifies `particles/PlayerLeaderboardRow` and the private `ListRow`
 * inside IhsTopScorers. Optional slots accommodate both:
 *
 *   - `rank` — leaderboard rank number on the left.
 *   - `nationalityCode` — small flag overlay on the crest.
 *   - `subtitle` — second line under the name (team or position).
 *   - `stats[]` — one or more stat cells on the right. Each item can
 *     render as a `<ValueBox>` (when `color` is supplied) or as a
 *     plain numeric column (no color → plain text, used by the
 *     leaderboard).
 *   - `animateValues` — opt-in `useCountUp` on every stat value, used
 *     by the leaderboard surface.
 *
 * The composed result is forward-compatible with both call sites; old
 * behaviour preserved.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { useCountUp } from '../../hooks/useCountUp.js';
import { CountryFlag } from './CountryFlag.js';
import { TeamCrest, useTeamVisualMode } from './TeamCrest.js';
import { ValueBox } from './ValueBox.js';
import styles from './PlayerRow.module.css';

/**
 * `display` may be any ReactNode at the component level; the wire shape
 * captures number-or-string so the LLM can build a row from data.
 */
const dataShape = z.object({
  player: z.object({
    name: z.string(),
    subtitle: z.string().optional(),
    nationalityCode: z.string().optional(),
  }),
  teamName: z.string(),
  teamBadge: z.string().url().optional(),
  rank: z.number().int().positive().optional(),
  stats: z
    .array(
      z.object({
        value: z.number(),
        display: z.union([z.string(), z.number()]).optional(),
        color: z.string().optional(),
        label: z.string().optional(),
      }),
    )
    .min(1),
  animateValues: z.boolean().optional(),
  crestSize: z.number().int().positive().optional(),
});

export const META: PrimitiveMeta = {
  name: 'player-row',
  description:
    'Compact row — optional rank, crest + flag, name+subtitle, N stat cells.',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'count-up',
  composes: ['team-crest', 'country-flag', 'value-box'],
};

export interface PlayerRowStat {
  /** Numeric value when known — drives `useCountUp` if `animateValues`. */
  value: number;
  /** Optional formatted override; otherwise the integer or rounded value renders. */
  display?: ReactNode;
  /** When set, the cell renders as a small ValueBox in this colour. */
  color?: string;
  /** Optional micro-label for ValueBox. */
  label?: string;
}

interface PlayerRowProps {
  player: {
    name: string;
    /** Second line under the name — team OR position. Consumer chooses. */
    subtitle?: string;
    nationalityCode?: string;
  };
  teamName: string;
  teamBadge?: string;
  /** Leaderboard rank — when set, renders a numeric cell at far-left. */
  rank?: number;
  stats: PlayerRowStat[];
  /** Opt-in count-up animation on stat values. Used by the leaderboard surface. */
  animateValues?: boolean;
  /** Crest diameter. Defaults to 28 (matches the IhsTopScorers list rows). */
  crestSize?: number;
}

interface AnimatedStatProps {
  stat: PlayerRowStat;
  enabled: boolean;
  delay: number;
}

function AnimatedStat({ stat, enabled, delay }: AnimatedStatProps): ReactElement {
  const animated = useCountUp({
    end: stat.value,
    duration: 500,
    delay,
    enabled,
  });
  // The legacy leaderboard cells use `number-pop` for the wiggle.
  const cellClass = `${styles.statCell} number-pop`;
  const style = { '--stagger-delay': `${delay}ms` } as CSSProperties;
  const rendered = stat.display ?? (enabled ? animated : stat.value);
  if (stat.color !== undefined) {
    return (
      <div className={cellClass} style={style}>
        <ValueBox
          value={rendered}
          color={stat.color}
          {...(stat.label !== undefined ? { label: stat.label } : {})}
          shape="rect"
          size="sm"
          variant="solid"
        />
      </div>
    );
  }
  return (
    <div className={cellClass} style={style}>
      {rendered}
    </div>
  );
}

export function PlayerRow({
  player,
  teamName,
  teamBadge,
  rank,
  stats,
  animateValues = false,
  crestSize = 28,
}: PlayerRowProps): ReactElement {
  // ADR-063 team-visual 'none': skip the crest cell (no empty puck).
  const teamVisual = useTeamVisualMode();
  return (
    <div className={styles.row}>
      {rank !== undefined ? (
        <div className={styles.rank}>{rank}</div>
      ) : null}
      {teamVisual !== 'none' ? (
      <div className={styles.crestCell}>
        <span className={styles.slot}>
          <TeamCrest
            {...(teamBadge !== undefined ? { src: teamBadge } : {})}
            name={teamName}
            size={crestSize}
          />
          {player.nationalityCode ? (
            <span className={styles.flagOverlay}>
              <CountryFlag code={player.nationalityCode} size={14} />
            </span>
          ) : null}
        </span>
      </div>
      ) : null}
      <div className={styles.body}>
        <div className={styles.name}>{player.name}</div>
        {player.subtitle !== undefined ? (
          <div className={styles.subtitle}>{player.subtitle}</div>
        ) : null}
      </div>
      {stats.map((stat, i) => (
        <AnimatedStat
          key={i}
          stat={stat}
          enabled={animateValues}
          delay={200 + i * 100}
        />
      ))}
    </div>
  );
}

export default PlayerRow;
